import React from "react";
import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useAdAccount } from "@/contexts/AdAccountContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Upload,
  X,
  CheckCircle,
  AlertCircle,
  Film,
  Image,
  Loader2,
  Clock,
  ArrowLeft,
  RotateCcw,
  HardDrive,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useGoogleDrivePicker, GoogleDriveFile } from "@/hooks/useGoogleDrivePicker";
import { UploadProgressTracker } from "@/components/creatives/UploadProgressTracker";
import { SafeProgressBoundary } from "@/components/creatives/SafeProgressBoundary";
import { useUploadProgressContext } from "@/contexts/UploadProgressContext";
import { useStagedFiles } from "@/hooks/useStagedFiles";
import { StagedFileGrid } from "@/components/creatives/StagedFileGrid";
import { AssetGroupingModal } from "@/components/creatives/AssetGroupingModal";
import { GroupedAsset, StagedFile } from "@/types/assetGrouping";

interface FileItem {
  id: string;
  file: File | null;
  thumbnailBlob: Blob | null;
  thumbnailPreview: string;
  dimensions: string;
  creative_type: "image" | "video";
  file_size_mb: number;
  status: "pending" | "uploading" | "success" | "error";
  progress: number;
  error?: string;
  fileUrl?: string;
  thumbnailUrl?: string;
  thumbnailPath?: string;
  isGoogleDrive?: boolean;
  filename?: string;
}

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_FILES_PER_BATCH = 50;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
const ACCEPTED_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/x-msvideo"];
const ACCEPTED_TYPES = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES];
const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;

const UploadCreatives = () => {
  const navigate = useNavigate();
  const { user, isMetaConnected, connectMeta } = useAuth();
  const { selectedAdAccountId, selectedAdAccountName, adAccounts, isLoading: isLoadingAccounts } = useAdAccount();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { openPicker, isLoading: isPickerLoading, error: pickerError } = useGoogleDrivePicker();
  const { startTracking, progress, stopTracking } = useUploadProgressContext();

  const selectedAdAccount = selectedAdAccountId || "";
  const groupInfoMapRef = useRef<Map<string, { group_id: string; group_type: string }>>(new Map());
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [isProcessingDriveFiles, setIsProcessingDriveFiles] = useState(false);
  const [showGroupingModal, setShowGroupingModal] = useState(false);

  const {
    stagedFiles,
    addFiles,
    removeFile: removeStagedFile,
    toggleSelect,
    selectAll,
    deselectAll,
    clearAll,
    selectedCount,
    totalCount,
  } = useStagedFiles();

  useEffect(() => {
    return () => {
      files.forEach((f) => {
        if (f.thumbnailPreview) URL.revokeObjectURL(f.thumbnailPreview);
      });
    };
  }, []);

  const extractImageDimensions = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        resolve(`${img.naturalWidth}x${img.naturalHeight}`);
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => {
        resolve("Unknown");
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const extractVideoDimensions = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.onloadedmetadata = () => {
        resolve(`${video.videoWidth}x${video.videoHeight}`);
        URL.revokeObjectURL(video.src);
      };
      video.onerror = () => {
        resolve("Unknown");
        URL.revokeObjectURL(video.src);
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const generateImageThumbnail = (file: File): Promise<{ blob: Blob | null; preview: string }> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve({ blob: null, preview: URL.createObjectURL(file) });
            return;
          }
          const size = 300;
          canvas.width = size;
          canvas.height = size;
          ctx.fillStyle = "#f1f5f9";
          ctx.fillRect(0, 0, size, size);
          const scale = Math.min(size / img.width, size / img.height);
          const x = (size - img.width * scale) / 2;
          const y = (size - img.height * scale) / 2;
          ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
          canvas.toBlob(
            (blob) => {
              const preview = canvas.toDataURL("image/jpeg", 0.8);
              URL.revokeObjectURL(img.src);
              resolve({ blob, preview });
            },
            "image/jpeg",
            0.8,
          );
        } catch {
          resolve({ blob: null, preview: URL.createObjectURL(file) });
        }
      };
      img.onerror = () => {
        resolve({ blob: null, preview: "" });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const generateVideoThumbnail = (file: File): Promise<{ blob: Blob | null; preview: string }> => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.muted = true;
      video.playsInline = true;
      video.onloadedmetadata = () => {
        video.currentTime = Math.min(1, video.duration / 2);
      };
      video.onseeked = () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve({ blob: null, preview: "" });
            return;
          }
          const size = 300;
          canvas.width = size;
          canvas.height = size;
          ctx.fillStyle = "#f1f5f9";
          ctx.fillRect(0, 0, size, size);
          const scale = Math.min(size / video.videoWidth, size / video.videoHeight);
          const x = (size - video.videoWidth * scale) / 2;
          const y = (size - video.videoHeight * scale) / 2;
          ctx.drawImage(video, x, y, video.videoWidth * scale, video.videoHeight * scale);
          canvas.toBlob(
            (blob) => {
              const preview = canvas.toDataURL("image/jpeg", 0.7);
              URL.revokeObjectURL(video.src);
              resolve({ blob, preview });
            },
            "image/jpeg",
            0.7,
          );
        } catch {
          resolve({ blob: null, preview: "" });
        }
      };
      video.onerror = () => {
        resolve({ blob: null, preview: "" });
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const processFile = async (file: File): Promise<FileItem | null> => {
    if (file.size > MAX_FILE_SIZE) {
      toast({ title: "File too large", description: `${file.name} exceeds 100MB limit`, variant: "destructive" });
      return null;
    }
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: `${file.name} is not a supported format`,
        variant: "destructive",
      });
      return null;
    }
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    const [dimensions, thumbnail] = await Promise.all([
      isImage ? extractImageDimensions(file) : extractVideoDimensions(file),
      isImage ? generateImageThumbnail(file) : generateVideoThumbnail(file),
    ]);
    return {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      thumbnailBlob: thumbnail.blob,
      thumbnailPreview: thumbnail.preview,
      dimensions,
      creative_type: isVideo ? "video" : "image",
      file_size_mb: Number((file.size / 1024 / 1024).toFixed(2)),
      status: "pending",
      progress: 0,
    };
  };

  const handleFiles = useCallback(
    async (fileList: FileList) => {
      const incoming = Array.from(fileList);
      const currentCount = files.length;
      const remaining = MAX_FILES_PER_BATCH - currentCount;

      if (remaining <= 0) {
        toast({ title: "Batch full", description: "Maximum 50 creatives per batch. Please upload the current batch first before adding more.", variant: "destructive" });
        return;
      }

      const accepted = incoming.slice(0, remaining);
      if (accepted.length < incoming.length) {
        toast({ title: "Batch full", description: "Maximum 50 creatives per batch. Please upload the current batch first before adding more.", variant: "destructive" });
      }

      addFiles(accepted);
      const newFiles: FileItem[] = [];
      for (const file of accepted) {
        const processed = await processFile(file);
        if (processed) newFiles.push(processed);
      }
      setFiles((prev) => {
        const updated = [...prev, ...newFiles];
        if (updated.length === MAX_FILES_PER_BATCH) {
          toast({ title: "Batch limit reached", description: "You've reached the maximum of 50 creatives per batch. Start this upload before adding more." });
        }
        return updated;
      });
    },
    [toast, addFiles, files.length],
  );

  const processGoogleDriveFiles = async (
    driveFiles: GoogleDriveFile[],
    accessToken: string | null,
  ): Promise<FileItem[]> => {
    const processedFiles: FileItem[] = [];
    for (const driveFile of driveFiles) {
      const isVideo = driveFile.mimeType.startsWith("video/");
      const isImage = driveFile.mimeType.startsWith("image/");
      if (!isVideo && !isImage) continue;
      try {
        if (!accessToken) {
          toast({
            title: "Authentication Error",
            description: "No Google Drive access token available.",
            variant: "destructive",
          });
          continue;
        }
        let downloadedFile: File;
        try {
          const response = await fetch(`https://www.googleapis.com/drive/v3/files/${driveFile.id}?alt=media`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          if (!response.ok) throw new Error(`Download failed: ${response.status} ${response.statusText}`);
          const blob = await response.blob();
          if (blob.size === 0) throw new Error("Downloaded file is empty (0 bytes)");
          downloadedFile = new File([blob], driveFile.name, { type: driveFile.mimeType });
        } catch (downloadError) {
          const errorMsg = downloadError instanceof Error ? downloadError.message : "Unknown download error";
          toast({ title: "Download Error", description: `${driveFile.name}: ${errorMsg}`, variant: "destructive" });
          continue;
        }
        const [dimensions, thumbnail] = await Promise.all([
          isImage ? extractImageDimensions(downloadedFile) : extractVideoDimensions(downloadedFile),
          isImage ? generateImageThumbnail(downloadedFile) : generateVideoThumbnail(downloadedFile),
        ]);
        processedFiles.push({
          id: `gdrive-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          file: downloadedFile,
          thumbnailBlob: thumbnail.blob,
          thumbnailPreview: thumbnail.preview,
          dimensions: dimensions || "Unknown",
          creative_type: isVideo ? "video" : "image",
          file_size_mb: Number((driveFile.sizeBytes / 1024 / 1024).toFixed(2)),
          status: "pending",
          progress: 0,
          isGoogleDrive: true,
          filename: driveFile.name,
        });
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : "Unknown error";
        toast({ title: "Processing Error", description: `${driveFile.name}: ${errorMsg}`, variant: "destructive" });
      }
    }
    return processedFiles;
  };

  const handleGoogleDriveImport = async () => {
    const currentCount = files.length;
    const remaining = MAX_FILES_PER_BATCH - currentCount;
    if (remaining <= 0) {
      toast({ title: "Batch full", description: "Maximum 50 creatives per batch. Please upload the current batch first before adding more.", variant: "destructive" });
      return;
    }

    setIsProcessingDriveFiles(true);
    try {
      const { files: selectedFiles, accessToken } = await openPicker();
      if (selectedFiles.length === 0) {
        setIsProcessingDriveFiles(false);
        return;
      }

      const accepted = selectedFiles.slice(0, remaining);
      if (accepted.length < selectedFiles.length) {
        toast({ title: "Batch full", description: "Maximum 50 creatives per batch. Please upload the current batch first before adding more.", variant: "destructive" });
      }

      toast({
        title: "Processing files",
        description: `Processing ${accepted.length} file(s) from Google Drive...`,
      });
      const processedFiles = await processGoogleDriveFiles(accepted, accessToken);
      if (processedFiles.length > 0) {
        setFiles((prev) => [...prev, ...processedFiles]);
        const fileObjects = processedFiles.map((pf) => pf.file).filter((f): f is File => f !== null);
        if (fileObjects.length > 0) addFiles(fileObjects);
        toast({ title: "Files added", description: `Added ${processedFiles.length} file(s) from Google Drive` });
      }
    } catch (error) {
      toast({
        title: "Import Error",
        description: error instanceof Error ? error.message : "Failed to import from Google Drive",
        variant: "destructive",
      });
    } finally {
      setIsProcessingDriveFiles(false);
    }
  };

  useEffect(() => {
    if (pickerError) toast({ title: "Google Drive Error", description: pickerError, variant: "destructive" });
  }, [pickerError, toast]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      e.target.value = "";
    }
  };

  const removeFile = (id: string) => {
    setFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file?.thumbnailPreview) URL.revokeObjectURL(file.thumbnailPreview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const updateFileItem = (id: string, updates: Partial<FileItem>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  };

  const uploadWithRetry = async (
    bucket: string,
    path: string,
    file: File | Blob,
    options?: { cacheControl?: string; contentType?: string },
    maxRetries = 3,
  ): Promise<void> => {
    let lastError: Error | null = null;
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (!sessionData?.session) throw new Error("No active session. Please log in again.");
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const { data, error } = await supabase.storage.from(bucket).upload(path, file, { ...options, upsert: true });
        if (error) throw error;
        return;
      } catch (error: unknown) {
        lastError = error instanceof Error ? error : new Error(String(error));
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, Math.min(1000 * Math.pow(2, attempt - 1), 5000)));
        }
      }
    }
    throw new Error(`Upload failed after ${maxRetries} attempts: ${lastError?.message || "Unknown error"}`);
  };

  const uploadToSupabase = async (
    fileItem: FileItem,
    onProgress?: (progress: number) => void,
  ): Promise<{ fileUrl: string; thumbnailUrl: string; thumbnailPath: string }> => {
    if (!fileItem.file) throw new Error("No file to upload");
    const timestamp = Date.now();
    const sanitizedFilename = fileItem.file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const originalPath = `${user?.id}/${timestamp}_${sanitizedFilename}`;
    const filenameWithoutExt = sanitizedFilename.replace(/\.[^/.]+$/, "");
    const thumbnailPath = `${user?.id}/${timestamp}_thumb_${filenameWithoutExt}.jpg`;
    onProgress?.(10);
    try {
      await uploadWithRetry("temp-uploads", originalPath, fileItem.file);
      onProgress?.(60);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      if (message.includes("Failed to fetch")) {
        throw new Error(
          `Network error uploading ${fileItem.file.name} (${fileItem.file_size_mb}MB). Check your connection and try again.`,
        );
      }
      throw new Error(`Failed to upload ${fileItem.file.name}: ${message}`);
    }
    let thumbnailUrl = "";
    if (fileItem.thumbnailBlob) {
      try {
        await uploadWithRetry("thumbnails", thumbnailPath, fileItem.thumbnailBlob, {
          cacheControl: "31536000",
          contentType: "image/jpeg",
        });
        const { data } = supabase.storage.from("thumbnails").getPublicUrl(thumbnailPath);
        thumbnailUrl = data.publicUrl;
      } catch (thumbnailError) {
        console.error("Thumbnail upload failed:", thumbnailError);
      }
    }
    onProgress?.(80);
    const {
      data: { publicUrl: fileUrl },
    } = supabase.storage.from("temp-uploads").getPublicUrl(originalPath);
    return { fileUrl, thumbnailUrl, thumbnailPath: fileItem.thumbnailBlob ? thumbnailPath : "" };
  };

  const handleUpload = async () => {
    const selectedNames = new Set(stagedFiles.filter((f) => f.selected).map((f) => f.name));
    const filesToUpload = files.filter(
      (f) => f.status === "pending" && selectedNames.has(f.filename || f.file?.name || ""),
    );
    if (!selectedAdAccount || filesToUpload.length === 0) return;

    setIsUploading(true);
    setUploadProgress({ current: 0, total: filesToUpload.length });
    const successfulFiles: FileItem[] = [];

    // Step 1: Upload all files to Supabase Storage
    for (let i = 0; i < filesToUpload.length; i++) {
      const fileItem = filesToUpload[i];
      updateFileItem(fileItem.id, { status: "uploading", progress: 5 });
      setUploadProgress({ current: i + 1, total: filesToUpload.length });
      try {
        const { fileUrl, thumbnailUrl, thumbnailPath } = await uploadToSupabase(fileItem, (p) =>
          updateFileItem(fileItem.id, { progress: p }),
        );
        updateFileItem(fileItem.id, { fileUrl, thumbnailUrl, progress: 80 });
        successfulFiles.push({ ...fileItem, fileUrl, thumbnailUrl, thumbnailPath });
      } catch (error) {
        const message = error instanceof Error ? error.message : "Upload failed";
        updateFileItem(fileItem.id, { status: "error", error: message });
        toast({ title: "Upload Failed", description: message, variant: "destructive" });
      }
    }

    // Step 2: Create batch + job records in DB, then trigger n8n worker
    if (successfulFiles.length > 0) {
      try {
        // Extract act_XXXXX from selectedAdAccount (may be "Name (act_123)" format)
        const adAccountMatch = selectedAdAccount.match(/\(([^)]+)\)$/);
        const metaAccountId = adAccountMatch ? adAccountMatch[1] : selectedAdAccount;

        // 2a. Create the batch record
        const { data: batchRecord, error: batchError } = await supabase
          .from("creative_upload_batches")
          .insert({
            user_id: user?.id as string,
            meta_account_id: metaAccountId,
            status: "queued",
            file_count: successfulFiles.length,
          })
          .select("id")
          .single();

        if (batchError || !batchRecord) {
          throw new Error("Failed to create upload batch: " + batchError?.message);
        }

        const batchId = batchRecord.id;

        // 2b. Create one job record per file
        const jobRecords = successfulFiles.map((f) => {
          const fileUrl = f.fileUrl || "";
          const storagePathMatch = fileUrl.match(/temp-uploads\/(.+)$/);
          const storagePath = storagePathMatch ? storagePathMatch[1] : "";
          return {
            batch_id: batchId,
            user_id: user?.id,
            meta_account_id: metaAccountId,
            creative_type: f.creative_type,
            storage_bucket: "temp-uploads",
            storage_path: storagePath,
            filename: f.filename || f.file?.name || "unknown",
            file_size_mb: f.file_size_mb || null,
            dimensions: f.dimensions || null,
            thumbnail_path: f.thumbnailPath || null,
            status: "queued",
            attempt_count: 0,
          };
        });

        const { error: jobsError } = await supabase.from("creative_upload_jobs").insert(jobRecords);
        if (jobsError) throw new Error("Failed to create upload jobs: " + jobsError.message);

        // 2c. Trigger n8n worker with batch_id
        const { data, error: proxyError } = await supabase.functions.invoke("n8n-proxy", {
          body: {
            action: "UPLOAD_CREATIVES_ASYNC",
            payload: {
              batch_id: batchId,
              user_id: user?.id,
              meta_account_id: metaAccountId,
            },
          },
        });

        if (proxyError) {
          toast({
            title: "Upload Error",
            description: "Failed to start upload: " + proxyError.message,
            variant: "destructive",
          });
          setIsUploading(false);
          return;
        }

        if (data?.batch_id) {
          startTracking(data.batch_id);
          toast({
            title: "Upload Started",
            description: `${successfulFiles.length} file(s) queued for upload. You can navigate away — uploads continue in the background.`,
          });

          // Update creative_metadata with group info from pairing flow
          if (groupInfoMapRef.current.size > 0) {
            for (const [filename, groupInfo] of groupInfoMapRef.current.entries()) {
              const { error: updateError } = await supabase
                .from("creative_metadata")
                .update({ group_id: groupInfo.group_id, group_type: groupInfo.group_type })
                .eq("user_id", user?.id)
                .ilike("filename", filename);
              if (updateError) console.error("[handleUpload] Failed to update group for", filename, updateError);
            }
            groupInfoMapRef.current.clear();
          }

          setFiles([]);
          clearAll();
        } else {
          toast({ title: "Error", description: "Unexpected response from server", variant: "destructive" });
        }
      } catch (error) {
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to start upload",
          variant: "destructive",
        });
      }
    }

    groupInfoMapRef.current.clear();
    setIsUploading(false);
  };

  const retryFailed = async () => {
    const failedFiles = files.filter((f) => f.status === "error");
    failedFiles.forEach((f) => updateFileItem(f.id, { status: "pending", error: undefined }));
    await handleUpload();
  };

  const clearCompleted = () => {
    setFiles((prev) => prev.filter((f) => f.status !== "success"));
  };

  const handleGroupingConfirm = async (result: { grouped: GroupedAsset[]; singles: StagedFile[] }) => {
    if (!selectedAdAccount) {
      toast({ title: "Error", description: "Please select an ad account first", variant: "destructive" });
      return;
    }
    const {
      data: { user: authUser },
    } = await supabase.auth.getUser();
    if (!authUser) {
      toast({ title: "Error", description: "Not authenticated", variant: "destructive" });
      return;
    }
    const adAccountMatch = selectedAdAccount.match(/\(([^)]+)\)$/);
    const adAccountId = adAccountMatch ? adAccountMatch[1] : selectedAdAccount;
    try {
      for (const group of result.grouped) {
        const { data: groupRecord, error: groupError } = await supabase
          .from("creative_groups")
          .insert({
            user_id: authUser.id,
            ad_account_id: adAccountId,
            group_name: group.group_name,
            group_type: group.group_type,
          })
          .select()
          .single();
        if (groupError) {
          console.error("Error creating group:", groupError);
          continue;
        }
        for (const asset of group.assets) {
          const filename = (asset as any).file?.name || (asset as any).file?.file?.name || (asset as any).name || "";
          if (filename)
            groupInfoMapRef.current.set(filename, { group_id: groupRecord.id, group_type: group.group_type });
        }
      }
      clearAll();
      if (files.length > 0) await handleUpload();
    } catch (error) {
      console.error("Error processing groups:", error);
      toast({ title: "Error", description: "Failed to create asset groups", variant: "destructive" });
    }
  };

  const truncateFilename = (name: string, maxLength = 20) => {
    if (name.length <= maxLength) return name;
    const ext = name.split(".").pop() || "";
    const base = name.slice(0, maxLength - ext.length - 4);
    return `${base}...${ext}`;
  };

  const getStatusIcon = (status: FileItem["status"]) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4 text-muted-foreground" />;
      case "uploading":
        return <Loader2 className="h-4 w-4 text-primary animate-spin" />;
      case "success":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "error":
        return <AlertCircle className="h-4 w-4 text-destructive" />;
    }
  };

  const failedCount = files.filter((f) => f.status === "error").length;
  const successCount = files.filter((f) => f.status === "success").length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/creatives/library")} className="mb-2">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold text-foreground">Upload Creatives</h1>
          <p className="text-muted-foreground">Upload images and videos to your Meta Ad Library</p>
        </div>
      </div>

      {/* Ad Account Status */}
      {!selectedAdAccount && (
        <Card className="p-6">
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <AlertCircle className="h-5 w-5" />
            <p className="text-sm font-medium">Please select an ad account from the sidebar</p>
          </div>
        </Card>
      )}

      {/* Upload Zone */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card
          className={cn(
            "p-6 border-2 border-dashed transition-colors cursor-pointer min-h-[200px] flex flex-col items-center justify-center",
            isDragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/50",
            (isUploading || isProcessingDriveFiles) && "pointer-events-none opacity-50",
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_TYPES.join(",")}
            onChange={handleFileInputChange}
            className="hidden"
          />
          <Upload className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-base font-medium text-foreground">Drag files here or click to browse</p>
          <p className="text-xs text-muted-foreground mt-2 text-center">JPG, PNG, GIF, MP4, MOV, AVI • Max 100MB</p>
        </Card>

        <Card
          className={cn(
            "p-6 border-2 border-dashed transition-colors cursor-pointer min-h-[200px] flex flex-col items-center justify-center",
            "border-muted-foreground/25 hover:border-primary hover:bg-primary/5",
            (isUploading || isProcessingDriveFiles || isPickerLoading) && "pointer-events-none opacity-50",
          )}
          onClick={handleGoogleDriveImport}
        >
          {isProcessingDriveFiles || isPickerLoading ? (
            <>
              <Loader2 className="h-10 w-10 text-muted-foreground mb-3 animate-spin" />
              <p className="text-base font-medium text-foreground">
                {isPickerLoading ? "Opening Google Drive..." : "Processing files..."}
              </p>
            </>
          ) : (
            <>
              <HardDrive className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-base font-medium text-foreground">Import from Google Drive</p>
              <p className="text-xs text-muted-foreground mt-2 text-center">Select images and videos from your Drive</p>
            </>
          )}
        </Card>
      </div>

      {/* Upload Progress Tracker */}
      <SafeProgressBoundary>
        {progress && <UploadProgressTracker progress={progress} onDismiss={() => stopTracking()} />}
      </SafeProgressBoundary>

      {/* Staged Files Grid */}
      {stagedFiles.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-foreground">Select Files for Upload</h2>
          <StagedFileGrid
            files={stagedFiles}
            onToggleSelect={toggleSelect}
            onRemove={removeStagedFile}
            onSelectAll={selectAll}
            onDeselectAll={deselectAll}
            selectedCount={selectedCount}
            totalCount={totalCount}
          />
        </div>
      )}

      {/* Upload Status Grid */}
      {files.length > 0 && files.some((f) => f.status !== "pending") && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">
              Upload Status ({files.filter((f) => f.status !== "pending").length})
            </h2>
            <div className="flex gap-2">
              {successCount > 0 && (
                <Button variant="outline" size="sm" onClick={clearCompleted}>
                  Clear Completed
                </Button>
              )}
              {failedCount > 0 && !isUploading && (
                <Button variant="outline" size="sm" onClick={retryFailed}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Retry Failed ({failedCount})
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {files
              .filter((f) => f.status !== "pending")
              .map((fileItem) => (
                <Card key={fileItem.id} className="relative overflow-hidden">
                  {fileItem.status !== "uploading" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(fileItem.id);
                      }}
                      className="absolute top-2 right-2 z-10 p-1 rounded-full bg-background/80 hover:bg-background transition-colors"
                    >
                      <X className="h-4 w-4 text-muted-foreground" />
                    </button>
                  )}
                  <div className="aspect-square bg-slate-100 dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                    {fileItem.thumbnailPreview ? (
                      <img
                        src={fileItem.thumbnailPreview}
                        alt={fileItem.filename || fileItem.file?.name || "Creative"}
                        className="w-full h-full object-contain"
                      />
                    ) : fileItem.isGoogleDrive ? (
                      <div className="flex flex-col items-center justify-center text-muted-foreground">
                        <HardDrive className="h-8 w-8 mb-1" />
                        <span className="text-xs">Google Drive</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center text-muted-foreground">
                        {fileItem.creative_type === "video" ? (
                          <Film className="h-12 w-12" />
                        ) : (
                          <Image className="h-12 w-12" />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span
                        className="text-sm font-medium text-foreground truncate"
                        title={fileItem.filename || fileItem.file?.name || "Unknown"}
                      >
                        {truncateFilename(fileItem.filename || fileItem.file?.name || "Unknown")}
                      </span>
                      <Badge variant={fileItem.creative_type === "video" ? "secondary" : "default"} className="ml-2">
                        {fileItem.creative_type === "video" ? (
                          <>
                            <Film className="h-3 w-3 mr-1" />
                            Video
                          </>
                        ) : (
                          <>
                            <Image className="h-3 w-3 mr-1" />
                            Image
                          </>
                        )}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        {fileItem.isGoogleDrive && <HardDrive className="h-3 w-3" />}
                        {fileItem.file_size_mb} MB
                      </span>
                      <span>{fileItem.dimensions}</span>
                    </div>
                    {(fileItem.status === "uploading" || fileItem.progress > 0) && fileItem.status !== "pending" && (
                      <Progress value={fileItem.progress} className="h-1" />
                    )}
                    <div className="flex items-center gap-2">
                      {getStatusIcon(fileItem.status)}
                      <span
                        className={cn(
                          "text-xs capitalize",
                          fileItem.status === "success" && "text-green-500",
                          fileItem.status === "error" && "text-destructive",
                          fileItem.status === "uploading" && "text-primary",
                          fileItem.status === "pending" && "text-muted-foreground",
                        )}
                      >
                        {fileItem.status}
                      </span>
                    </div>
                    {fileItem.error && (
                      <p className="text-xs text-destructive truncate" title={fileItem.error}>
                        {fileItem.error}
                      </p>
                    )}
                  </div>
                </Card>
              ))}
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="text-sm text-muted-foreground">
          {isUploading && uploadProgress.total > 0 && (
            <span>
              Uploading {uploadProgress.current} of {uploadProgress.total}...
            </span>
          )}
        </div>
        <Button
          size="lg"
          onClick={() => setShowGroupingModal(true)}
          disabled={!selectedAdAccount || selectedCount === 0 || isUploading}
          className="min-w-[200px]"
        >
          {isUploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Continue to Upload ({selectedCount} selected)
            </>
          )}
        </Button>
      </div>

      {/* Asset Grouping Modal */}
      <AssetGroupingModal
        open={showGroupingModal}
        onOpenChange={setShowGroupingModal}
        files={stagedFiles.filter((f) => f.selected)}
        onConfirm={handleGroupingConfirm}
      />
    </div>
  );
};

export default UploadCreatives;
